// Phase 12 (ADR-014) — multi-role Zod schemas shared between backend
// (request validation + response typing) and mobile (RoleSelect, AddRole,
// the KYC screens, the role switcher).
//
// `UserRoleAssignment` replaces the single `User.role` enum: a user holds
// 1..N roles, exactly one `is_primary`. Role-specific data lives INLINE on
// the assignment (REDESIGN §6.1 closed decision — no satellite tables).
//
// KYC follows the ADR-013 Nivel A pattern: each uploaded document carries a
// `KycDocStatus`; the JIT gate only passes on `VERIFIED`.
import { z } from 'zod';
import { USER_ROLES, MOBILE_SELECTABLE_ROLES } from './auth.js';
import { VatRegimeSchema } from './marketplace.js';
// ─── Enums ──────────────────────────────────────────────────────────────────
/** Review state of a single uploaded KYC document (vet licence, transporter
 *  authorisation, vehicle ficha, competence cert). Mirrors the Prisma
 *  `KycDocStatus` enum. */
export const KycDocStatusSchema = z.enum([
    'NOT_STARTED', // declared but no document uploaded yet
    'PENDING_REVIEW', // uploaded + HEAD-verified, waiting on admin
    'VERIFIED', // admin approved — the JIT gate opens
    'REJECTED', // admin rejected; the user may re-upload
]);
/** Natural person vs legal entity (REDESIGN §9.2). Mirrors Prisma `EntityType`. */
export const EntityTypeSchema = z.enum(['PERSOA_FISICA', 'PERSOA_XURIDICA']);
/** Lifecycle of a VET ↔ explotación link. Mirrors Prisma `FarmVetAccessStatus`. */
export const FarmVetAccessStatusSchema = z.enum(['PENDING', 'ACTIVE', 'REVOKED']);
const UserRoleSchema = z.enum(USER_ROLES);
const MobileSelectableRoleSchema = z.enum(MOBILE_SELECTABLE_ROLES);
// ─── UserRoleAssignment ─────────────────────────────────────────────────────
/** Wire shape of a single role assignment. All role-specific fields are
 *  nullable because one physical table backs every role — a FARMER
 *  assignment simply leaves the VET / TRANSPORTISTA / BUYER columns null. */
export const UserRoleAssignmentSchema = z.object({
    id: z.string().uuid(),
    role: UserRoleSchema,
    is_primary: z.boolean(),
    /** Non-null → the role is soft-deactivated (REDESIGN §11): hidden from the
     *  switcher and excluded from the JWT `roles[]`. */
    deactivated_at: z.string().datetime({ offset: true }).nullable(),
    created_at: z.string().datetime({ offset: true }),
    // VET
    vet_license: z.string().nullable(),
    vet_license_status: KycDocStatusSchema.nullable(),
    // TRANSPORTISTA (MR504A)
    transporter_auth_no: z.string().nullable(),
    transporter_auth_type: z.string().nullable(),
    transporter_auth_expiry: z.string().datetime({ offset: true }).nullable(),
    transporter_auth_status: KycDocStatusSchema.nullable(),
    vehicle_plate: z.string().nullable(),
    vehicle_auth_no: z.string().nullable(),
    vehicle_auth_status: KycDocStatusSchema.nullable(),
    competence_cert_no: z.string().nullable(),
    competence_cert_expiry: z.string().datetime({ offset: true }).nullable(),
    competence_cert_status: KycDocStatusSchema.nullable(),
    // BUYER
    vat_regime: VatRegimeSchema.nullable(),
    // Phase 12 M7 — set by admin.ts when one of the role's docs is REJECTED.
    // Shared across the row (re-upload clears it), and the mobile KycUpload
    // screen surfaces it on whichever doc is currently REJECTED.
    reject_reason: z.string().nullable(),
});
/** `GET /api/v1/users/me/roles` — the user's roles + which one is primary. */
export const RolesResponseSchema = z.object({
    roles: z.array(UserRoleAssignmentSchema),
    primary_role: UserRoleSchema,
});
/** `POST /api/v1/users/me/roles` — add a role (triggers its setup flow). */
export const AddRoleRequestSchema = z
    .object({
    role: MobileSelectableRoleSchema,
})
    .strict();
/** `PATCH /api/v1/users/me/roles/primary` — change which role loads first. */
export const SetPrimaryRoleRequestSchema = z
    .object({
    role: UserRoleSchema,
})
    .strict();
/** `PATCH /api/v1/users/me/roles/:role` — update the inline data on the
 *  assignment (transporter auth numbers, vehicle plate, vet licence #, BUYER
 *  VAT regime). Documents themselves come through the M7 KYC upload route;
 *  this endpoint persists the *declared* values that the user enters during
 *  TransporterSetup / VetSetup / BUYER setup. Every field is optional so the
 *  same body shape works for partial edits later (e.g. plate change).
 *
 *  The `*_status` columns are NOT exposed here — they transition only through
 *  the KYC upload + admin review flow, never directly from the user. */
/** Spanish veterinary collegiate number — accepts pure digits (4-6 typical),
 *  optional province-letter prefix (e.g. "C-1234", "G1234", "PO 1234"). The
 *  goal is NOT a strict registry lookup (deferred to V2 when COVE publishes
 *  an API) but to reject obvious garbage like "asdfasdf" that an admin would
 *  otherwise have to reject manually. Allowed chars: A-Z + digits + `-` + `/`
 *  + space; the value MUST contain at least 3 consecutive digits. */
const VET_LICENSE_ALLOWED_RE = /^[A-Za-z0-9\s\-/]+$/;
const VET_LICENSE_HAS_NUMBER_RE = /\d{3,}/;
export const VetLicenseSchema = z
    .string()
    .trim()
    .min(3)
    .max(12)
    .refine((v) => VET_LICENSE_ALLOWED_RE.test(v), {
    message: 'invalid_chars',
})
    .refine((v) => VET_LICENSE_HAS_NUMBER_RE.test(v), {
    message: 'missing_number',
});
export const UpdateRoleAssignmentRequestSchema = z
    .object({
    // VET
    vet_license: VetLicenseSchema.optional(),
    // TRANSPORTISTA (MR504A)
    transporter_auth_no: z.string().min(1).max(40).optional(),
    transporter_auth_type: z.string().min(1).max(40).optional(),
    transporter_auth_expiry: z.string().datetime({ offset: true }).optional(),
    vehicle_plate: z.string().min(1).max(16).optional(),
    vehicle_auth_no: z.string().min(1).max(40).optional(),
    competence_cert_no: z.string().min(1).max(40).optional(),
    competence_cert_expiry: z.string().datetime({ offset: true }).optional(),
    // BUYER
    vat_regime: VatRegimeSchema.optional(),
})
    .strict();
/** RoleSelect (onboarding) — multi-select of mobile roles + the primary pick.
 *  The primary must be one of the selected roles (refined). */
export const RoleSelectionRequestSchema = z
    .object({
    roles: z.array(MobileSelectableRoleSchema).min(1).max(MOBILE_SELECTABLE_ROLES.length),
    primary_role: MobileSelectableRoleSchema,
})
    .strict()
    .refine((v) => v.roles.includes(v.primary_role), {
    message: 'primary_role must be one of the selected roles',
    path: ['primary_role'],
});
// ─── Contract identity (REDESIGN §9.2) ──────────────────────────────────────
/** Seller/buyer fiscal identity collected during setup — feeds the Documenso
 *  compraventa template. When `entity_type` is PERSOA_XURIDICA, `legal_name`
 *  (razón social) is required and `dni` is interpreted as a CIF. */
export const EntityIdentityRequestSchema = z
    .object({
    entity_type: EntityTypeSchema,
    legal_name: z.string().min(1).max(160).optional(),
    fiscal_address: z.string().min(1).max(240).optional(),
})
    .strict()
    .refine((v) => v.entity_type === 'PERSOA_FISICA' || !!v.legal_name, {
    message: 'legal_name is required when entity_type is PERSOA_XURIDICA',
    path: ['legal_name'],
});
// ─── FarmVetAccess (ADR-014) ────────────────────────────────────────────────
/** Wire shape of a VET ↔ explotación link. */
export const FarmVetAccessSchema = z.object({
    id: z.string().uuid(),
    farm_id: z.string().uuid(),
    vet_id: z.string().uuid().nullable(),
    status: FarmVetAccessStatusSchema,
    invite_code: z.string(),
    invite_expires_at: z.string().datetime({ offset: true }),
    linked_at: z.string().datetime({ offset: true }).nullable(),
    created_at: z.string().datetime({ offset: true }),
});
/** `POST /api/v1/vet/farm-links` — a VET redeems a single-use invite code. */
export const RedeemVetInviteRequestSchema = z
    .object({
    invite_code: z.string().min(6).max(64),
})
    .strict();
/** `GET /api/v1/farms/:id/vets` — FARMER lists pending invites + ACTIVE vets
 *  for their farm. The PENDING row reveals the `invite_code` so the FARMER
 *  can re-share it; ACTIVE rows expose the linked `vet` (id + name) so the
 *  ManageVets screen can label them.
 *
 *  Phase 25 iter-21 (CR3) — `signed_events_count` is the count of events
 *  this VET has authored on this farm with a `vet_signature`. Only set on
 *  ACTIVE rows (PENDING vets haven't redeemed yet). Used by the ManageVets
 *  surface to render an audit cue per vet. */
export const FarmVetAccessListItemSchema = FarmVetAccessSchema.extend({
    vet: z
        .object({
        id: z.string().uuid(),
        name: z.string(),
    })
        .nullable(),
    signed_events_count: z.number().int().nonnegative().nullable().optional(),
    last_signed_at: z.string().datetime({ offset: true }).nullable().optional(),
});
export const FarmVetAccessListResponseSchema = z.object({
    accesses: z.array(FarmVetAccessListItemSchema),
});
/** `GET /api/v1/vet/farms` — VET sees the farms they have ACTIVE access to. */
export const VetFarmSummarySchema = z.object({
    access_id: z.string().uuid(),
    farm: z.object({
        id: z.string().uuid(),
        name: z.string(),
        municipality: z.string(),
        type: z.enum(['CARNE', 'LECHE', 'MIXTA']),
        rega_code: z.string().nullable(),
    }),
    linked_at: z.string().datetime({ offset: true }).nullable(),
});
export const VetFarmsResponseSchema = z.object({
    farms: z.array(VetFarmSummarySchema),
});
/** Single-use invite codes (VET links + Movement assignments) expire 7 days
 *  after creation — phase-12 PRD §4 closed decision. */
export const INVITE_CODE_TTL_SECONDS = 7 * 24 * 60 * 60;
// ─── KYC document uploads (Phase 12 M7) ─────────────────────────────────────
//
// Each role exposes a fixed set of `KycDocKind`s; the upload route maps
// `(role, doc_kind)` → S3 key `kyc/<userId>/<role>/<doc_kind>.pdf` and the
// `*_doc_url` + `*_status` columns on `UserRoleAssignment`. Re-uploads
// overwrite the same key, so a REJECTED → re-upload flow is idempotent.
/** VET → only the colexiado licence PDF. */
export const VetDocKindSchema = z.enum(['vet_license']);
/** TRANSPORTISTA → three MR504A documents. */
export const TransporterDocKindSchema = z.enum([
    'transporter_auth',
    'vehicle_auth',
    'competence_cert',
]);
/** Union of every `(role, doc_kind)` the upload route accepts. */
export const KycDocKindSchema = z.union([VetDocKindSchema, TransporterDocKindSchema]);
/** Roles that can upload KYC documents (BUYER has none in V1 — its KYC is
 *  the DIDIT session + SEPA mandate handled elsewhere). */
export const KycDocRoleSchema = z.enum(['VET', 'TRANSPORTISTA']);
// Phase 13 M6 — Hard upload cap for KYC PDFs. 10 MB lets a multi-page
// licence scan in colour fit comfortably while still keeping a single
// abusive POST from filling the bucket. Enforced via the zod refinement
// below (zod rejects requests > MAX with 400) and by signing the presigned
// PUT URL with `ContentLength: content_length` so S3 enforces too.
export const KYC_DOC_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
/** `POST /api/v1/kyc/:role/documents` request — body picks which document
 *  and declares the exact PDF byte count so the backend can sign the
 *  presigned URL with `ContentLength` (S3 then rejects mismatched bodies). */
export const KycDocUploadRequestSchema = z
    .object({
    doc_kind: KycDocKindSchema,
    content_length: z.number().int().positive().max(KYC_DOC_UPLOAD_MAX_BYTES),
})
    .strict();
/** Pre-signed upload URL + canonical URL — same shape as the marketplace
 *  doc upload response, intentionally so the mobile uploader is generic. */
export const KycDocUploadResponseSchema = z.object({
    upload_url: z.string().url(),
    canonical_url: z.string().url(),
    expires_at: z.string().datetime({ offset: true }),
    /** Mirrors `KYC_DOC_UPLOAD_MAX_BYTES` so a future bump only needs a backend release. */
    max_bytes: z.number().int().positive(),
});
/** `PATCH /api/v1/kyc/:role/documents/complete` — the client tells the API
 *  the upload finished; the API HEAD-verifies the object and flips the
 *  matching `*_status` column to `PENDING_REVIEW`. */
export const KycDocCompleteRequestSchema = z
    .object({
    doc_kind: KycDocKindSchema,
    canonical_url: z.string().url(),
})
    .strict();
/** Wire shape returned by upload + complete — minimal view of the assignment
 *  the mobile screen needs to re-render after the action. */
export const KycDocStateSchema = z.object({
    assignment_id: z.string().uuid(),
    role: UserRoleSchema,
    doc_kind: KycDocKindSchema,
    status: KycDocStatusSchema,
    doc_url: z.string().url().nullable(),
    reject_reason: z.string().nullable(),
});
// ─── Admin review queue (Phase 12 M7) ───────────────────────────────────────
/** One row in the admin pending-review queue. `pending_docs` is the list of
 *  doc kinds currently in `PENDING_REVIEW` for this assignment.
 *  `doc_urls` maps each pending kind to its canonical S3 URL so the admin
 *  web can render an "Abrir documento" link without a second round-trip;
 *  the value is null when the user hasn't uploaded that kind yet (shouldn't
 *  happen for a kind that's in `pending_docs`, defensive). */
export const AdminKycPendingItemSchema = z.object({
    assignment_id: z.string().uuid(),
    user: z.object({
        id: z.string().uuid(),
        name: z.string(),
        email: z.string().email().nullable(),
    }),
    role: KycDocRoleSchema,
    pending_docs: z.array(KycDocKindSchema).min(1),
    doc_urls: z.record(KycDocKindSchema, z.string().url().nullable()).optional(),
    /** Free-text fields the gandeiro/professional typed alongside the docs.
     *  Surfaced to the admin so a vet_license "asdfasdf" can be rejected with
     *  a reason. */
    vet_license: z.string().nullable().optional(),
    transporter_auth_no: z.string().nullable().optional(),
    vehicle_plate: z.string().nullable().optional(),
    vehicle_auth_no: z.string().nullable().optional(),
    competence_cert_no: z.string().nullable().optional(),
    /** Earliest `updated_at` across the pending docs — drives FIFO ordering. */
    oldest_pending_at: z.string().datetime({ offset: true }),
});
export const AdminKycPendingResponseSchema = z.object({
    rows: z.array(AdminKycPendingItemSchema),
});
/** Admin's verdict on one `(assignment, doc_kind)` pair. `reject_reason` is
 *  required when `decision === 'REJECTED'` so the user knows what to fix. */
export const AdminKycDecisionRequestSchema = z
    .object({
    doc_kind: KycDocKindSchema,
    decision: z.enum(['VERIFIED', 'REJECTED']),
    reject_reason: z.string().min(1).max(500).optional(),
})
    .strict()
    .refine((v) => v.decision !== 'REJECTED' || !!v.reject_reason, {
    message: 'reject_reason is required when decision is REJECTED',
    path: ['reject_reason'],
});
/** Pre-signed URL TTL for KYC uploads — same 10 minutes as marketplace. */
export const KYC_DOC_UPLOAD_URL_TTL_SECONDS = 600;
//# sourceMappingURL=role.js.map